// Added by Sejune Oh on 2019-06-25
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef, RefObject } from 'react'
import { action, makeObservable, observable } from 'mobx'
import { observer, inject } from 'mobx-react'
import localeCode from 'locale-code'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import {
  UserAccessLevelProps,
  UILanguageProps,
  ReactSelectEvent,
  TextFormatter,
  InputBlurValidator,
  USER_ACCESS_LEVEL,
  ENUM_USER_ACCESS_LEVEL,
  ReactChangeWithValue,
  MgmtValidator,
  ReactFormEvent,
  FormatterProps,
} from '@/helper/define'
import { getFormData, getQueryPath } from '@/helper/util'
import Logger from '@/helper/Logger'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { UserBaseCProps } from '../../../UserBase/UserBaseContainer'
import UserForm from './UserForm'
import withUserValidator from '../../../UserValidator'
import { UserStore, DisplableUserElementProps } from '@/stores/mgmt/user'
import ProductStore from '@/stores/ProductStore'
import { SystemConfigStore } from '@/stores/mgmt/config'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface UserFormCProps extends UserBaseCProps, MgmtValidator, FormatterProps {
  user?: UserStore
  emailRef: RefObject<any>
  usernameRef: RefObject<any>
  pwdRef: RefObject<any>
  confirmPwdRef: RefObject<any>
  product?: ProductStore
  systemConfig?: SystemConfigStore
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('user', 'product', 'systemConfig')
@observer
class UserFormContainer extends Component<UserFormCProps> {
  uiLanguage?: UILanguageProps
  accessLevel?: UserAccessLevelProps
  userElement?: DisplableUserElementProps = this.props.userElement
  mounted: boolean = false
  isActivated: string = 'yes'
  requiredSysAdminuser: boolean = false

  _accessLevelList: Array<UserAccessLevelProps> = []
  _uiLanguageList: Array<UILanguageProps> = []
  _formSubmitBtnRef = createRef<HTMLButtonElement>()
  _formId = 'u-f'

  constructor(props: UserFormCProps) {
    super(props)
    makeObservable(this, {
      uiLanguage: observable,
      accessLevel: observable,
      userElement: observable,
      mounted: observable,
      isActivated: observable,
      requiredSysAdminuser: observable,
      componentDidMount: action,
      _setUserElement: action,
      _setUILanguage: action,
      _setAccessLevel: action,
      _setActive: action,
      _initWithUserLevelNames: action,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  componentDidMount = async () => {
    await this.props.systemConfig!.initialize()

    this._uiLanguageList = this.props.systemConfig!.systemConfigsOption.languageConfig!.map((item) => {
      const nativeName = localeCode.getLanguageNativeName(item.lang_code)
      const countryCode = localeCode.getCountryCode(item.lang_code)
      const langCode = localeCode.getLanguageCode(item.lang_code)

      return {
        id: item.lang_code,
        label: `${nativeName} (${langCode}-${countryCode})`,
      }
    })

    const sysAdminAcessLevel = USER_ACCESS_LEVEL.AccessLevelSysAdmin.enumValue
    const getSysAdminuserQuery = getQueryPath({
      filter: JSON.stringify({
        $op: 'and',
        access_level: sysAdminAcessLevel,
        activated: true,
      }),
    })
    const sysAdmins = await this.props.user!.getElements(getSysAdminuserQuery)
    await this._initWithUserLevelNames()

    if (this.props.userElement) {
      await this._initWithUserInfo()
      this.requiredSysAdminuser =
        (sysAdmins.length === 1 && this.userElement!.access_level === sysAdminAcessLevel) ||
        this.userElement!.id! === '1'
    } else this._initDefault()

    this.mounted = true
    this.props.onChildComponentMount('userForm')
  }

  render() {
    const { validator, blurValidator: baseBlurValidator, ...others } = this.props
    const blurValidator = {
      ...baseBlurValidator,
      userNameBlurValidator: this._userNameBlurValidator,
      emailBlurValidator: this._emailBlurValidator,
    }
    const formatter = {
      accessLevelFormatter: this._accessLevelFormatter,
      uiLanguageFormatter: this._uiLanguageFormatter,
    }

    return (
      this.mounted && (
        <UserForm
          {...others}
          validator={validator}
          blurValidator={blurValidator}
          formatter={formatter}
          userElement={this.userElement!}
          uiLanguage={this.uiLanguage!}
          accessLevel={this.accessLevel!}
          onUILanguageChange={this._handleUILanguageChange}
          onAccessLevelChange={this._handleAccessLevelChange}
          accessLevelList={this._accessLevelList}
          uiLanguageList={this._uiLanguageList}
          formSubmitBtnRef={this._formSubmitBtnRef}
          formId={this._formId}
          isActivated={this.isActivated}
          onRadioChange={this._handleRadioChange}
          onSubmit={this._handleSubmit}
          requiredSysAdminuser={this.requiredSysAdminuser}
        />
      )
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setUserElement = (element: DisplableUserElementProps) => {
    this.userElement = element
  }

  _setUILanguage = (uiLang: UILanguageProps) => {
    this.uiLanguage = uiLang
  }

  _setAccessLevel = (level: UserAccessLevelProps) => {
    this.accessLevel = level
  }

  _setActive = (active: string) => {
    this.isActivated = active
  }

  _initWithUserLevelNames = () => {
    if (!this.props.product!.userLevelNames) {
      this._accessLevelList = [USER_ACCESS_LEVEL.AccessLevelSysAdmin]
      return
    }

    const acceeLevelList: Array<UserAccessLevelProps> = []
    for (let enumKey in this.props.product!.userLevelNames) {
      acceeLevelList.push({
        ...ENUM_USER_ACCESS_LEVEL[enumKey],
        label: this.props.product!.userLevelNames[enumKey],
      })
    }
    this._accessLevelList = acceeLevelList
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  _initWithUserInfo = () => {
    const { ui_language, access_level } = this.userElement!
    const accessLevel = this._accessLevelList.find(({ enumValue }) => enumValue === access_level)
    const uiLang = ui_language ? this._uiLanguageList.find(({ id }) => id === ui_language) : this._uiLanguageList[0]

    /** set default language - English */
    const defaultLanguage: UILanguageProps[] = this._uiLanguageList.filter((lang) => lang.id === 'en-US')
    this._setUILanguage(uiLang || defaultLanguage[0])
    this._setAccessLevel(accessLevel || this._accessLevelList[0])
  }

  _initDefault = () => {
    this._setUserElement({
      username: '',
      display_name: '',
      email: '',
      description: '',
    })

    /** set default language - English */
    const defaultLanguage: UILanguageProps[] = this._uiLanguageList.filter((lang) => lang.id === 'en-US')
    this._setUILanguage(defaultLanguage[0])
    this._setAccessLevel(this._accessLevelList[0])
  }

  _handleSubmit: ReactFormEvent = (e) => {
    e.preventDefault()
  }

  _handleUILanguageChange: ReactSelectEvent = ({ target }) => {
    const langId = target.value
    const selectedLangId = this._uiLanguageList.find(({ id }) => id === langId)
    this._setUILanguage(selectedLangId!)
  }

  _handleAccessLevelChange: ReactSelectEvent = ({ target }) => {
    const accessLevelEnum = parseInt(target.value!)
    const selectedAccessLevel = this._accessLevelList.find(({ enumValue }) => enumValue === accessLevelEnum)
    this._setAccessLevel(selectedAccessLevel!)
  }

  _handleRadioChange: ReactChangeWithValue = ({ currentTarget }, value) => {
    this._setActive(value)
  }

  _accessLevelFormatter: TextFormatter<UserAccessLevelProps> = (item: UserAccessLevelProps) => {
    return `${item.label}`
  }

  _uiLanguageFormatter: TextFormatter<UILanguageProps> = (item: UILanguageProps) => {
    return `${item.label}`
  }

  _userNameBlurValidator: InputBlurValidator = async (value) => {
    const pwdRef = this.props.pwdRef.current!
    if (pwdRef && pwdRef.value !== '') pwdRef.callValidator()
    if (this.props.userId && this.userElement!.username === value) return [false]

    return this.props.blurValidator.userNameBlurValidator(value)
  }

  _emailBlurValidator: InputBlurValidator = async (value) => {
    const pwdRef = this.props.pwdRef.current!
    if (pwdRef && pwdRef.value !== '') pwdRef.callValidator()
    if (this.props.userId && this.userElement!.email === value) return [false]

    return this.props.blurValidator.emailBlurValidator(value)
  }

  getFormData = () => {
    try {
      const form = document.getElementById(this._formId) as HTMLFormElement
      const userElement = getFormData<DisplableUserElementProps>(form) as DisplableUserElementProps

      userElement.access_level = Number(userElement.access_level)
      userElement.activated = this.isActivated === 'yes'

      delete userElement.confirm_pwd

      return userElement
    } catch (err) {
      Logger.errLog(err)
      this.props.showSnackbar!('err', err.message)
      return null
    }
  }
}

export default withUserValidator()(UserFormContainer)

